import { useCallback, useSyncExternalStore } from 'react';

/** Cuánto puede crecer el contenido de la página. */
export type ContentWidth = 'full' | 'wide' | 'normal';

/**
 * Misma clave y mismo formato que sporttia-competitions, que guarda esta
 * preferencia con el middleware `persist` de zustand: un envoltorio
 * `{"state":{"width":"full"},"version":0}`. Las dos aplicaciones se sirven desde
 * el mismo dominio, así que compartiendo clave y formato la preferencia viaja de
 * una a otra; con la cadena a pelo ellos no sabrían leerla.
 */
const STORAGE_KEY = 'sporttia-content-width';
const STORE_VERSION = 0;
const DEFAULT_WIDTH: ContentWidth = 'full';
const WIDTHS: ContentWidth[] = ['full', 'wide', 'normal'];

const listeners = new Set<() => void>();

function isContentWidth(value: unknown): value is ContentWidth {
    return typeof value === 'string' && WIDTHS.includes(value as ContentWidth);
}

function read(): ContentWidth {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return DEFAULT_WIDTH;

    try {
        const parsed = JSON.parse(stored) as { state?: { width?: unknown } };

        return isContentWidth(parsed?.state?.width)
            ? parsed.state!.width!
            : DEFAULT_WIDTH;
    } catch {
        // Valor guardado por versiones anteriores, que escribían la cadena sola.
        return isContentWidth(stored) ? stored : DEFAULT_WIDTH;
    }
}

/**
 * Refleja la preferencia en `<html data-content-width>`, que es lo que lee la
 * variable CSS `--content-max-width` (ver src/index.css).
 */
function applyContentWidth(width: ContentWidth): void {
    document.documentElement.dataset.contentWidth = width;
}

/** Aplica la preferencia guardada. Se llama una vez al arrancar la aplicación. */
export function initContentWidth(): void {
    applyContentWidth(read());
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/**
 * Preferencia global de ancho de contenido, persistida en localStorage. Se usa
 * un store mínimo (useSyncExternalStore) para no arrastrar una dependencia extra
 * sólo por esto.
 */
export function useContentWidth(): {
    width: ContentWidth;
    setWidth: (width: ContentWidth) => void;
} {
    const width = useSyncExternalStore(subscribe, read, () => DEFAULT_WIDTH);

    const setWidth = useCallback((next: ContentWidth): void => {
        localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ state: { width: next }, version: STORE_VERSION }),
        );
        applyContentWidth(next);
        listeners.forEach((listener) => listener());
    }, []);

    return { width, setWidth };
}
